"use client";

import Image from "next/image";
import React, { useCallback, useEffect, useState } from "react";

const TITLE_LINE_COLOR = "#2e1065";
const SLIDE_DURATION_MS = 500;
const CARD_WIDTH = 280;
const CARD_HEIGHT = 360;
const CARD_GAP = 28;

/** Circle background colors in sequence (card 0 → first, card 1 → second, … then repeat) */
const CIRCLE_COLORS = ["#6E8CFB", "#00A26B", "#76153D", "#446882"];

export interface WProcessStep2Card {
  imageUrl?: string;
  imageAlt?: string;
  title?: string;
  description?: string;
}

export interface WProcessStep2SectionProps {
  heading?: string;
  backgroundColor?: string;
  maxWidth?: "default" | "wide" | "full";
  cards?: WProcessStep2Card[];
}

export default function WProcessStep2Section({
  heading,
  backgroundColor,
  maxWidth = "wide",
  cards = [],
}: WProcessStep2SectionProps) {
  const isFullWidth = maxWidth === "full";
  const contentMaxWidthClass = isFullWidth
    ? "max-w-none w-full"
    : maxWidth === "wide"
      ? "max-w-6xl"
      : "max-w-4xl";
  const sectionMaxWidthClass = isFullWidth
    ? "max-w-none"
    : maxWidth === "wide"
      ? "max-w-6xl"
      : "max-w-4xl";
  const sectionBg =
    backgroundColor != null && String(backgroundColor).trim() !== ""
      ? String(backgroundColor).trim()
      : undefined;

  const displayTitle = heading != null && String(heading).trim() !== "" ? String(heading).trim() : "";
  const showTitleBlock = displayTitle !== "";

  const validCards = cards.filter(
    (c) => c?.title != null && String(c.title).trim() !== ""
  );
  const length = validCards.length;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [suppressTransition, setSuppressTransition] = useState(false);

  const goNext = useCallback(() => {
    if (length <= 1) return;
    setCurrentIndex((i) => Math.min(length, i + 1));
  }, [length]);

  const goPrev = useCallback(() => {
    if (length <= 1) return;
    setCurrentIndex((i) => Math.max(0, i - 1));
  }, [length]);

  useEffect(() => {
    if (length <= 1) return;
    if (currentIndex >= length) {
      const t = setTimeout(() => {
        setSuppressTransition(true);
        setCurrentIndex(0);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setSuppressTransition(false));
        });
      }, SLIDE_DURATION_MS);
      return () => clearTimeout(t);
    }
  }, [length, currentIndex]);

  const trackCards = length > 0 ? [...validCards, ...validCards] : [];
  const slideTransition = suppressTransition
    ? "none"
    : `${SLIDE_DURATION_MS}ms ease-out`;

  const arrowButtonClass =
    "flex shrink-0 items-center justify-center p-1 text-gray-500 transition-opacity hover:opacity-80 focus:outline-none focus:ring-0 disabled:opacity-40";

  if (validCards.length === 0) return null;

  return (
    <section
      className={`mx-auto w-full py-6 md:py-8 ${sectionMaxWidthClass}`}
      style={sectionBg ? { backgroundColor: sectionBg } : undefined}
      aria-label={displayTitle || "Process steps"}
    >
      <div className={`mx-auto w-full px-4 md:px-6 ${contentMaxWidthClass}`}>
        {showTitleBlock && (
          <div className="mb-6 grid w-full grid-cols-[1fr_auto_1fr] items-center gap-3">
            <div
              className="h-0.5 w-[35%] min-w-[80px] justify-self-end"
              style={{
                background: `linear-gradient(to right, transparent, ${TITLE_LINE_COLOR})`,
              }}
              aria-hidden
            />
            <h2 className="text-center font-extrabold uppercase tracking-wide text-violet-950 md:text-xl lg:text-2xl">
              {displayTitle}
            </h2>
            <div
              className="h-0.5 w-[35%] min-w-[80px] justify-self-start"
              style={{
                background: `linear-gradient(to right, ${TITLE_LINE_COLOR}, transparent)`,
              }}
              aria-hidden
            />
          </div>
        )}

        <div className={`mx-auto w-full rounded-xl p-3 md:p-4 ${isFullWidth ? "max-w-none" : contentMaxWidthClass}`}>
          <div className="flex items-stretch gap-2 md:gap-3">
            <button
              type="button"
              onClick={goPrev}
              className={arrowButtonClass}
              aria-label="Previous"
              disabled={length <= 1}
            >
              <svg
                className="size-8 md:size-10"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {/* Mobile: one full-width card at a time */}
            <div className="relative min-w-0 flex-1 overflow-hidden md:hidden">
              <div
                className="flex"
                style={{
                  transition: `transform ${slideTransition}`,
                  transform: `translateX(-${currentIndex * 100}%)`,
                  minHeight: CARD_HEIGHT,
                }}
              >
                {trackCards.map((card, index) => {
                  const colorIndex = (index % length) % CIRCLE_COLORS.length;
                  const circleColor = CIRCLE_COLORS[colorIndex];
                  return (
                  <div
                    key={index}
                    className="group flex min-w-full flex-shrink-0 flex-col items-center justify-start rounded-xl bg-white px-4 py-5 text-center shadow-sm transition-colors duration-200 hover:bg-amber-50"
                    style={{ minHeight: CARD_HEIGHT }}
                  >
                    {card.imageUrl && (
                      <div
                        className="mb-4 flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-full transition-transform duration-300 group-hover:scale-110 md:size-24"
                        style={{ backgroundColor: circleColor }}
                      >
                        <Image
                          src={card.imageUrl}
                          alt={card.imageAlt || card.title || "Card"}
                          width={48}
                          height={48}
                          className="object-contain brightness-0 invert"
                          sizes="48px"
                          unoptimized={
                            card.imageUrl.startsWith("http") &&
                            !card.imageUrl.includes("trichos")
                          }
                        />
                      </div>
                    )}
                    <h3 className="mb-2 text-lg font-bold text-gray-900">
                      {card.title}
                    </h3>
                    {card.description && card.description.trim() !== "" && (
                      <p className="mb-4 text-sm leading-relaxed text-gray-600">
                        {card.description.trim()}
                      </p>
                    )}
                    <div
                      className="mt-auto flex size-7 shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-bold text-gray-600"
                      aria-hidden
                    >
                      {(index % length) + 1}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>

            {/* Desktop: fixed-width cards, scroll by one */}
            <div className="relative hidden min-w-0 flex-1 overflow-hidden md:block">
              <div
                className="flex justify-center"
                style={{ minHeight: CARD_HEIGHT }}
              >
                <div
                  className="flex"
                  style={{
                    transition: `transform ${slideTransition}`,
                    transform: `translateX(-${currentIndex * (CARD_WIDTH + CARD_GAP)}px)`,
                    gap: CARD_GAP,
                  }}
                >
                  {trackCards.map((card, index) => {
                    const colorIndex = (index % length) % CIRCLE_COLORS.length;
                    const circleColor = CIRCLE_COLORS[colorIndex];
                    return (
                    <div
                      key={index}
                      className="group flex flex-shrink-0 flex-col items-center justify-start rounded-xl bg-white px-4 py-5 text-center shadow-sm transition-colors duration-200 hover:bg-amber-50"
                      style={{
                        width: CARD_WIDTH,
                        minHeight: CARD_HEIGHT,
                      }}
                    >
                      {card.imageUrl && (
                        <div
                          className="mb-4 flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-full transition-transform duration-300 group-hover:scale-110 md:size-24"
                          style={{ backgroundColor: circleColor }}
                        >
                          <Image
                            src={card.imageUrl}
                            alt={card.imageAlt || card.title || "Card"}
                            width={48}
                            height={48}
                            className="object-contain brightness-0 invert"
                            sizes="48px"
                            unoptimized={
                              card.imageUrl.startsWith("http") &&
                              !card.imageUrl.includes("trichos")
                            }
                          />
                        </div>
                      )}
                      <h3 className="mb-2 text-lg font-bold text-gray-900">
                        {card.title}
                      </h3>
                      {card.description && card.description.trim() !== "" && (
                        <p className="mb-4 text-sm leading-relaxed text-gray-600">
                          {card.description.trim()}
                        </p>
                      )}
                      <div
                        className="mt-auto flex size-7 shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-bold text-gray-600"
                        aria-hidden
                      >
                        {(index % length) + 1}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={goNext}
              className={arrowButtonClass}
              aria-label="Next"
              disabled={length <= 1}
            >
              <svg
                className="size-8 md:size-10"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
